import BomPortfolioPage from './pages/BomPortfolioPage';
import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import { Provider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import { SnackbarProvider } from 'notistack';
import { AuthProvider, useAuth, ProtectedRoute, LoginPage, RegisterPage, ForgotPasswordPage, SetPasswordPage, ProfilePage, SettingsPage } from '@ciea.ai/smart-auth-lib';
import { store } from '@ciea.ai/smart-infra-navigation';

import theme from './theme/theme';
import AppLayout from './components/layout/AppLayout';
import { ProcessMonitorProvider } from './contexts/ProcessMonitorContext';
import { CATALOG_PERMISSIONS } from './constants/permissions';
import { NoCatalogAccessPage } from './components/auth/NoCatalogAccessPage';
import { PermissionDeniedRedirect } from './components/auth/PermissionDeniedRedirect';
import { SnackbarBridge } from './components/notifications/SnackbarBridge';
import { AppSnackbar } from './components/notifications/AppSnackbar';
import {
  DashboardPage,
  PartsPage,
  PartDetailPage,
  ChangeDetailPage,
  ImpactedAssembliesPage,
  ProblemReportsPage,
  DrawingWorkspacePage,
  CrossReferencesPage,
  CrossReferenceComparePage,
  BOMPage,
  CreateBomPage,
  BOMDetailPage,
  BomAnalyticsPage,
  PartnersPage,
  PartnerPortalPage,
  DomainConfigPage,
} from './pages';
import {
  AnalyticsHub,
  ExecutiveCommandCenter,
  OperationsIntelligence,
  ProcurementIntelligence,
  SupplyChainIntelligence,
  FinanceIntelligence,
  DataQualityDashboard,
  SpendAnalytics,
} from './pages/analytics';
import {
  EngineeringReviewQueuePage,
  EngineeringInboxMockPage,
  OrchestratorWorkItemDetailMockPage,
  EngineerReviewQueueMockPage,
} from './pages/engineering';
import {
  OrchestratorE2ETestPage,
  BuyerReviewQueuePage,
  EngineerReviewQueuePage,
} from './pages/orchestrator';

// Heavy route-only chunks — lazy-loaded so they don't bloat the initial bundle.
// CreatePartnerPage ~855KB, MROAnalyticsDashboard ~444KB (recharts),
// EngineeringReviewDetailPage drags in CADViewer + three.js (~1.26MB).
const CreatePartnerPage = React.lazy(() => import('./pages/CreatePartnerPage'));
const AdminArea = React.lazy(() => import('./pages/admin/AdminArea').then((m) => ({ default: m.AdminArea })));
const CloudArea = React.lazy(() => import('./pages/admin/AdminArea').then((m) => ({ default: m.CloudArea })));
const AgentsFleetPage = React.lazy(() => import('./pages/admin/AgentsFleetPage').then((m) => ({ default: m.AgentsFleetPage })));
const AiCostAllCompaniesPage = React.lazy(() => import('./pages/insights/ai-cost/AiCostAllCompaniesPage').then((m) => ({ default: m.AiCostAllCompaniesPage })));
const MROAnalyticsDashboard = React.lazy(() => import('./pages/analytics/MROAnalyticsDashboard'));
const AiCostOverviewPage = React.lazy(() => import('./pages/insights/ai-cost/AiCostOverviewPage'));
const AiCostBomPage = React.lazy(() => import('./pages/insights/ai-cost/AiCostBomPage'));
const AiCostPartPage = React.lazy(() => import('./pages/insights/ai-cost/AiCostPartPage'));
const AiCostAccuracyPage = React.lazy(() => import('./pages/insights/ai-cost/AiCostAccuracyPage'));
const CatalogAnalyticsPage = React.lazy(() => import('./pages/analytics/catalog/CatalogAnalyticsPage'));
const InsightsOperationsPage = React.lazy(() => import('./pages/insights/OperationsPage'));
const InsightsProcurementPage = React.lazy(() => import('./pages/insights/ProcurementPage'));
const InsightsEngineeringPage = React.lazy(() => import('./pages/insights/EngineeringPage'));
const InsightsExplorePage = React.lazy(() => import('./pages/insights/ExplorePage'));
const InsightsAskPage = React.lazy(() => import('./pages/insights/AskPage'));
const InsightsWebsitePage = React.lazy(() => import('./pages/insights/WebsitePage'));
const InsightsCatalogPage = React.lazy(() => import('./pages/insights/CatalogHealthPage'));
const InsightsCatalogPartsPage = React.lazy(() => import('./pages/insights/CatalogPartsPage'));
// Once on any Insights page, load the others in the background so switching is instant.
const preloadInsights = () => {
  void import('./pages/insights/OperationsPage');
  void import('./pages/insights/ProcurementPage');
  void import('./pages/insights/EngineeringPage');
  void import('./pages/insights/ExplorePage');
  void import('./pages/insights/AskPage');
  void import('./pages/insights/CatalogHealthPage');
  void import('./pages/insights/CatalogPartsPage');
  void import('./pages/insights/ai-cost/AiCostOverviewPage');
};
const InsightsPreload: React.FC = () => {
  React.useEffect(() => {
    const id = window.setTimeout(preloadInsights, 300);
    return () => window.clearTimeout(id);
  }, []);
  return <Outlet />;
};
const EngineeringReviewDetailPage = React.lazy(
  () => import('./pages/engineering/EngineeringReviewDetailPage'),
);
const BuyerReviewDetailPage = React.lazy(
  () => import('./pages/buyer/BuyerReviewDetailPage'),
);
import {
  BuyerTriageQueuePage,
  BuyerApprovalQueuePage,
  BuyerWorklistMockPage,
} from './pages/buyer';
import {
  BomUploadPage,
  BomPreviewPage,
  BomIntelligenceDashboard,
} from './pages/bom-intelligence';
import RfqCenter, { RfqPageRedirect } from './components/buyer-agent/rfq-center/RfqCenter';
import GetQuotesPage from './components/buyer-agent/rfq-center/GetQuotesPage';
import CatalogMigrationPage from './pages/CatalogMigrationPage';
import AnalyticsDataPage from './pages/data-operations/AnalyticsDataPage';
import RfqCollabShell from './pages/RfqCollabShell';

const CommunicationTemplatesPage = React.lazy(() => import('./pages/CommunicationTemplatesPage'));
const MasterSyncPage = React.lazy(() => import('./pages/MasterSyncPage'));

// Federation Wrappers
import CMSRouteWrapper from './components/federation/CMSRouteWrapper';
import WorkflowsRouteWrapper from './components/federation/WorkflowsRouteWrapper';

// Create React Query client with defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      retry: 0,
    },
  },
});

// Error Boundary Component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: 20,
          textAlign: 'center',
        }}>
          <h1>Something went wrong</h1>
          <p style={{ color: '#666', marginBottom: 20 }}>
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              fontSize: 16,
              cursor: 'pointer',
              backgroundColor: '#0EA5E9',
              color: 'white',
              border: 'none',
              borderRadius: 8,
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Placeholder component for routes not yet implemented
const PlaceholderPage: React.FC<{ title: string }> = ({ title }) => (
  <div style={{ padding: 40, textAlign: 'center' }}>
    <h2>{title}</h2>
    <p style={{ color: '#666' }}>This page is under construction</p>
  </div>
);

// Loading spinner component
const LoadingSpinner = () => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
    <CircularProgress />
  </Box>
);

// RequireAuth component - redirects to login if not authenticated
const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

// Permission-OR gate (the auth lib's ProtectedRoute only does AND across requiredPermissions).
// Used for the work-item review route where either reviewer role can act.
const AnyPermissionRoute: React.FC<{
  permissions: string[];
  children: React.ReactNode;
  fallback: React.ReactNode;
}> = ({ permissions, children, fallback }) => {
  const { hasAnyPermission } = useAuth();
  if (!hasAnyPermission(permissions)) {
    return <>{fallback}</>;
  }
  return <>{children}</>;
};

// Authenticated routes - wrapped with AppLayout
const AuthenticatedRoutes = () => (
  <Routes>
    {/* Profile routes from auth-lib */}
    <Route path="/profile" element={<ProfilePage />} />
    <Route path="/settings" element={<SettingsPage />} />

    {/* EXTERNAL supplier portal — PUBLIC (magic-link only, no platform login).
        Lives outside the catalog:read ProtectedRoute layout so a logged-out
        supplier can open it; authorization is the :token alone. Same shell as
        the internal RFQ, in guest mode (token-scoped data, no internal chrome). */}
    <Route path="/portal/rfq/:token" element={<RfqCollabShell guest />} />

    {/* Main Layout Routes — every child requires catalog:read at minimum.
        Stricter permissions (review/admin) are stacked on specific branches below. */}
    <Route
      path="/"
      element={
        <ProtectedRoute
          requiredPermissions={[CATALOG_PERMISSIONS.READ]}
          fallback={<NoCatalogAccessPage />}
        >
          <AppLayout />
        </ProtectedRoute>
      }
    >
      {/* Dashboard */}
      <Route index element={<DashboardPage />} />

      {/* Parts */}
      <Route path="parts">
        <Route index element={<PartsPage />} />
        <Route path="new" element={<PlaceholderPage title="Add New Part" />} />
        <Route path="import" element={<PlaceholderPage title="Import Parts" />} />
        <Route path=":partId" element={<PartDetailPage />} />
        {/* ONE workspace component — single parts get the canvas; assemblies
            additionally get the level navigation (children from the BOM). */}
        <Route path=":partId/drawing" element={<DrawingWorkspacePage />} />
      </Route>

      {/* A change has its own URL because it is SENT to somebody: an
          approver receiving "please approve ECO-2026-0044" has to be able to
          land on it, which a dialog cannot offer. Flat rather than nested
          under the bill, because the number is what gets quoted, and because
          a change will eventually touch more than one bill. */}
      {/* Before :changeId, which would otherwise read "problems" as an id. */}
      <Route path="changes/problems" element={<ProblemReportsPage />} />
      <Route path="changes/:changeId" element={<ChangeDetailPage />} />
      {/* The assemblies carrying a change from below, searchable. */}
      <Route path="changes" element={<ImpactedAssembliesPage />} />

      {/* Catalog Migration — customer catalog import cockpit */}
      <Route path="catalog-migration" element={<CatalogMigrationPage />} />

      {/* Data Operations > Analytics data: the warehouse refresh, run by hand and tracked */}
      <Route path="analytics-data" element={<AnalyticsDataPage />} />

      {/* Cross-References */}
      <Route path="cross-references">
        <Route index element={<CrossReferencesPage />} />
        <Route path="new" element={<PlaceholderPage title="Add Cross-Reference" />} />
        <Route path="compare/:sourcePartId/:targetPartId" element={<CrossReferenceComparePage />} />
      </Route>

      {/* Redirect legacy /suppliers to /partners */}
      <Route path="suppliers" element={<Navigate to="/partners" replace />} />
      <Route path="suppliers/*" element={<Navigate to="/partners" replace />} />

      {/* Bill of Materials */}
      <Route path="bom">
        <Route index element={<BomPortfolioPage />} />
        {/* The former BOM list, kept while the portfolio settles in. */}
        <Route path="list" element={<BOMPage />} />
        <Route path="analytics" element={<BomAnalyticsPage />} />
        <Route path="new" element={<CreateBomPage />} />
        <Route path=":bomId" element={<BOMDetailPage />} />
      </Route>

      {/* BOM Intelligence (uVision MVP) */}
      <Route path="bom-intelligence">
        <Route path="upload" element={<BomUploadPage />} />
        <Route path="preview/:analysisId" element={<BomPreviewPage />} />
        <Route path="dashboard/:bomId" element={<BomIntelligenceDashboard />} />
      </Route>

      {/* Orchestrator (PRD-OWI-001) */}
      <Route path="orchestrator">
        <Route
          path="e2e-test"
          element={
            <ProtectedRoute
              requiredPermissions={[CATALOG_PERMISSIONS.ADMIN]}
              fallback={<PermissionDeniedRedirect feature="Orchestrator E2E Test" requiredPermissions={[CATALOG_PERMISSIONS.ADMIN]} />}
            >
              <OrchestratorE2ETestPage />
            </ProtectedRoute>
          }
        />
        {/* Routing rules now live in Domain Config (Orchestrator tab). */}
        <Route path="routing-rules" element={<Navigate to="/domain-config" replace />} />
      </Route>

      {/* Engineering Review (PRD-EAO-001) */}
      <Route
        path="engineering"
        element={
          <ProtectedRoute
            requiredPermissions={[CATALOG_PERMISSIONS.REVIEW_ENGINEER]}
            fallback={<PermissionDeniedRedirect feature="Engineering Review" requiredPermissions={[CATALOG_PERMISSIONS.REVIEW_ENGINEER]} />}
          >
            <Outlet />
          </ProtectedRoute>
        }
      >
        <Route index element={<EngineerReviewQueuePage />} />
        <Route path="review-queue" element={<EngineerReviewQueuePage />} />
        {/* Old per-cross-ref queue deprecated — redirect to new (PRD §D20) */}
        <Route path="queue" element={<Navigate to="/engineering/review-queue" replace />} />
        <Route path="inbox-mock" element={<EngineeringInboxMockPage />} />
        <Route path="inbox-mock/detail" element={<OrchestratorWorkItemDetailMockPage />} />
        <Route path="review-queue-mock" element={<EngineerReviewQueueMockPage />} />
        <Route path="reviews/:reviewId" element={<EngineeringReviewDetailPage />} />
        <Route path="assignment-rules" element={<PlaceholderPage title="Assignment Rules" />} />
      </Route>

      {/* Buyer Review (PRD-BAO-001 → migrated to PRD-OWI-001 §D20) */}
      <Route
        path="buyer"
        element={
          <ProtectedRoute
            requiredPermissions={[CATALOG_PERMISSIONS.REVIEW_BUYER]}
            fallback={<PermissionDeniedRedirect feature="Buyer Review" requiredPermissions={[CATALOG_PERMISSIONS.REVIEW_BUYER]} />}
          >
            <Outlet />
          </ProtectedRoute>
        }
      >
        <Route index element={<BuyerReviewQueuePage />} />
        <Route path="worklist" element={<BuyerReviewQueuePage />} />
        {/* Old triage queue deprecated — redirect to new (PRD §D20) */}
        <Route path="triage" element={<Navigate to="/buyer/worklist" replace />} />
        <Route path="worklist-mock" element={<BuyerWorklistMockPage />} />
        {/* Final approval is P2 — gated to admin until two-stage flow is validated */}
        <Route
          path="approvals"
          element={
            <ProtectedRoute
              requiredPermissions={[CATALOG_PERMISSIONS.ADMIN]}
              fallback={<PermissionDeniedRedirect feature="Final Buyer Approvals" requiredPermissions={[CATALOG_PERMISSIONS.ADMIN]} />}
            >
              <BuyerApprovalQueuePage />
            </ProtectedRoute>
          }
        />
        <Route path="reviews/:reviewId" element={<BuyerReviewDetailPage />} />
        <Route path="assignment-rules" element={<PlaceholderPage title="Buyer Assignment Rules" />} />
      </Route>

      {/* Insights: one home per responsibility plus Explore and Ask (catalog:read, inherited from /) */}
      <Route path="insights" element={<InsightsPreload />}>
        <Route index element={<Navigate to="/insights/operations" replace />} />
        <Route path="operations" element={<InsightsOperationsPage />} />
        <Route path="procurement" element={<InsightsProcurementPage />} />
        <Route path="engineering" element={<InsightsEngineeringPage />} />
        <Route path="catalog" element={<InsightsCatalogPage />} />
        <Route path="catalog/parts" element={<InsightsCatalogPartsPage />} />
        <Route path="explore" element={<InsightsExplorePage />} />
        <Route path="ask" element={<InsightsAskPage />} />
        <Route path="website" element={<InsightsWebsitePage />} />
        {/* AI cost, from the warehouse: overview, then one BOM, then one part */}
        <Route path="ai-cost" element={<AiCostOverviewPage />} />
        <Route path="ai-cost/bom/:bomId" element={<AiCostBomPage />} />
        <Route path="ai-cost/part/:partId" element={<AiCostPartPage />} />
        <Route path="ai-cost/accuracy" element={<AiCostAccuracyPage />} />
      </Route>

      {/* Analytics — most are catalog:read (inherited from /).
          Supply Chain / Finance / MRO are largely mock data and stay admin-only in P1. */}
      <Route path="analytics">
        <Route index element={<AnalyticsHub />} />
        {/* Catalog Analytics: live warehouse reports (src/pages/analytics/catalog) */}
        <Route path="catalog" element={<CatalogAnalyticsPage />} />
        <Route path="executive" element={<ExecutiveCommandCenter />} />
        <Route path="operations" element={<OperationsIntelligence />} />
        <Route path="procurement" element={<ProcurementIntelligence />} />
        <Route
          path="supply-chain"
          element={
            <ProtectedRoute
              requiredPermissions={[CATALOG_PERMISSIONS.ADMIN]}
              fallback={<PermissionDeniedRedirect feature="Supply Chain Intelligence" requiredPermissions={[CATALOG_PERMISSIONS.ADMIN]} />}
            >
              <SupplyChainIntelligence />
            </ProtectedRoute>
          }
        />
        <Route
          path="finance"
          element={
            <ProtectedRoute
              requiredPermissions={[CATALOG_PERMISSIONS.ADMIN]}
              fallback={<PermissionDeniedRedirect feature="Finance Intelligence" requiredPermissions={[CATALOG_PERMISSIONS.ADMIN]} />}
            >
              <FinanceIntelligence />
            </ProtectedRoute>
          }
        />
        <Route path="spend" element={<SpendAnalytics />} />
        <Route path="data-quality" element={<DataQualityDashboard />} />
        <Route
          path="mro"
          element={
            <ProtectedRoute
              requiredPermissions={[CATALOG_PERMISSIONS.ADMIN]}
              fallback={<PermissionDeniedRedirect feature="MRO Analytics" requiredPermissions={[CATALOG_PERMISSIONS.ADMIN]} />}
            >
              <MROAnalyticsDashboard />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* AI Cost dashboard */}
      <Route path="ai-cost" element={<Navigate to="/insights/ai-cost" replace />} />

      {/* Contract Management System (Federation) */}
      <Route path="cms/*" element={<CMSRouteWrapper />} />
      {/* Approvals inbox, approval workflows and runs (smart-workflow-engine v2) */}
      <Route path="workflows/*" element={<WorkflowsRouteWrapper />} />

      {/* Partners (local - consolidated from Partner Network) */}
      <Route path="partners">
        <Route index element={<PartnersPage />} />
        {/* Comm. templates is admin-only — not exposed to design partner in P1 */}
        <Route
          path="templates"
          element={
            <ProtectedRoute
              requiredPermissions={[CATALOG_PERMISSIONS.ADMIN]}
              fallback={<PermissionDeniedRedirect feature="Communication Templates" requiredPermissions={[CATALOG_PERMISSIONS.ADMIN]} />}
            >
              <CommunicationTemplatesPage />
            </ProtectedRoute>
          }
        />
        <Route path="new" element={<CreatePartnerPage />} />
        <Route path=":partnerId" element={<PartnerPortalPage />} />
      </Route>

      {/* RFQ Dashboard (Buyer Agent Phase 2) */}
      <Route path="rfq" element={<RfqCenter />} />
      {/* Get quotes for a BOM line: suppliers, then lines and files, then the draft. */}
      <Route path="rfq/new" element={<GetQuotesPage />} />
      {/* Canonical RFQ detail page — mounts ThreadDetailView for the linked
          thread; bookmarkable URL. Reached from Orchestrator card, RFQ
          dashboard row click, and supplier-comms tab "Open as RFQ". */}
      <Route path="rfq/:rfqId" element={<RfqPageRedirect />} />
      {/* RFQ collaboration shell — two tabs (Navigation | Workspace). The
          Workspace tab embeds the real DrawingWorkspacePage. */}
      <Route path="rfq/:rfqId/workspace" element={<RfqCollabShell />} />

      {/* Administration (people, roles, company, plan) and Cloud Manager (uVizion staff).
          The organization-management API decides what each signed-in user may do. */}
      {/* Administration > Sourcing databases: the company's own distributor accounts. A catalog page, so it is
          declared before admin/* (more specific, so it wins). */}
      <Route path="admin/*" element={<AdminArea />} />
      {/* Cloud Manager > AI agents: every company's agents at a glance, uVizion staff only. A company's
          own agent screens open inside its company page. More specific than cloud/*, so it wins. */}
      <Route path="cloud/agents" element={<AgentsFleetPage companyHref={(id) => `/cloud/companies/${encodeURIComponent(id)}/agents`} />} />
      {/* Cloud Manager > AI cost, all companies; each company's own AI cost opens inside its company page. */}
      <Route path="cloud/ai-cost" element={<AiCostAllCompaniesPage companyHref={(id) => `/cloud/companies/${encodeURIComponent(id)}/ai-cost`} />} />
      <Route path="cloud/*" element={<CloudArea />} />

      {/* Domain Configuration Admin — catalog:admin only */}
      <Route
        path="domain-config"
        element={
          <ProtectedRoute
            requiredPermissions={[CATALOG_PERMISSIONS.ADMIN]}
            fallback={<PermissionDeniedRedirect feature="Domain Configuration" requiredPermissions={[CATALOG_PERMISSIONS.ADMIN]} />}
          >
            <DomainConfigPage />
          </ProtectedRoute>
        }
      />
      {/* Part pipelines are edited in the workflow engine (Process Designer, Part workflows). */}
      <Route path="workflow-designer" element={<Navigate to="/workflows/workflows?view=pipelines" replace />} />
      <Route
        path="master-catalog/sync"
        element={
          <ProtectedRoute
            requiredPermissions={[CATALOG_PERMISSIONS.ADMIN]}
            fallback={<PermissionDeniedRedirect feature="Master Catalog Sync" requiredPermissions={[CATALOG_PERMISSIONS.ADMIN]} />}
          >
            <MasterSyncPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  </Routes>
);

// Main App Routes component with auth handling
const AppRoutes = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Routes>
      {/* Public auth routes */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />}
      />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      {/* Invite and password reset links: set a password, then sign in. Public: the token is the key. */}
      <Route path="/accept-invite" element={<SetPasswordPage />} />
      <Route path="/reset-password" element={<SetPasswordPage />} />

      {/* Protected routes */}
      <Route
        path="/*"
        element={
          <RequireAuth>
            <ProcessMonitorProvider>
              <Suspense fallback={<LoadingSpinner />}>
                <AuthenticatedRoutes />
              </Suspense>
            </ProcessMonitorProvider>
          </RequireAuth>
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <Provider store={store}>
        <AuthProvider>
          <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                {/* notistack — platform-standard toast provider. maxSnack=3 prevents
                    UI flood; preventDuplicate stops repeated permission-denied
                    spam when a user mashes a denied button. SnackbarBridge wires
                    the imperative API for non-React callers (e.g. axios 403 handler).
                    AppSnackbar replaces notistack's flat-saturated default with a
                    bounded card + accent-rail design (close-X is inside the card). */}
                <SnackbarProvider
                  maxSnack={3}
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  autoHideDuration={4500}
                  preventDuplicate
                  Components={{
                    success: AppSnackbar,
                    info: AppSnackbar,
                    warning: AppSnackbar,
                    error: AppSnackbar,
                  }}
                >
                  <SnackbarBridge />
                  <BrowserRouter basename={window.location.pathname.startsWith('/catalog') ? '/catalog' : undefined}>
                    <AnimatePresence mode="wait">
                      <AppRoutes />
                    </AnimatePresence>
                  </BrowserRouter>
                </SnackbarProvider>
              </ThemeProvider>

              {/* React Query Devtools - only in development */}
              {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
            </QueryClientProvider>
          </ErrorBoundary>
        </AuthProvider>
      </Provider>
    </HelmetProvider>
  );
};

export default App;
